// frontend/src/pages/ProfilePage.tsx
//
// User profile & settings page. Contains all settings previously in the modal,
// plus language selection and user identity info.

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useCreatorConnect } from '../hooks/useCreatorConnect';
import { getCountryName } from '../lib/countries';
import { getTierDisplayName } from '../lib/supabase';
import { PAYMENTS_DISABLED } from '../lib/payments';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  User, Eye, EyeOff, Check, DollarSign, ExternalLink,
  Clock, Globe, AtSign, Link as LinkIcon, CreditCard, ArrowLeft,
} from 'lucide-react';

const PENDING_APP_QUERY_KEY = 'llambada_pending_app_query';

// Username validation following Twitter/X rules
const validateUsername = (username: string): { valid: boolean; error?: string } => {
  if (!username) return { valid: true };
  if (username.length < 4) return { valid: false, error: 'Username must be at least 4 characters' };
  if (username.length > 15) return { valid: false, error: 'Username must be 15 characters or less' };
  if (!/^[a-zA-Z0-9_]+$/.test(username)) return { valid: false, error: 'Only letters, numbers, and underscores allowed' };
  if (username.startsWith('_')) return { valid: false, error: 'Username cannot start with underscore' };
  if (/^[0-9]+$/.test(username)) return { valid: false, error: 'Username cannot be only numbers' };
  return { valid: true };
};

// Sanitize API key - remove any whitespace, newlines, etc.
const sanitizeApiKey = (key: string): string => {
  return key.replace(/\s+/g, '').trim();
};

// Validate API key format (Anthropic keys start with "sk-ant-")
const isValidApiKeyFormat = (key: string): boolean => {
  if (!key) return true;
  return key.startsWith('sk-ant-') && key.length > 20;
};

export function ProfilePage() {
  const { user, profile, updateProfile, isAdmin, creatorFeaturesEnabled, refreshProfile, subscriptionTier } = useAuth();
  const { tt } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: connectLoading, error: connectError, startOnboarding, openDashboard, clearError: clearConnectError } = useCreatorConnect();

  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [kimiApiKey, setKimiApiKey] = useState('');
  const [showKimiApiKey, setShowKimiApiKey] = useState(false);
  const [glmApiKey, setGlmApiKey] = useState('');
  const [showGlmApiKey, setShowGlmApiKey] = useState(false);
  const [minimaxApiKey, setMinimaxApiKey] = useState('');
  const [showMinimaxApiKey, setShowMinimaxApiKey] = useState(false);
  const [autoSubmitErrors, setAutoSubmitErrors] = useState(false);
  const [nickname, setNickname] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isPublicProfile, setIsPublicProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [connectSuccess, setConnectSuccess] = useState(false);

  const hasApiKey = !!profile?.anthropic_api_key;
  const hasKimiApiKey = !!profile?.kimi_api_key;
  const hasGlmApiKey = !!profile?.glm_api_key;
  const isConnected = !!profile?.stripe_onboarding_completed;
  const isChargesEnabled = !!profile?.stripe_charges_enabled;

  useEffect(() => {
    document.title = tt('Profile & Settings') + ' - Llambada';
    return () => { document.title = 'Llambada'; };
  }, [tt]);

  // Initialize form state from profile
  useEffect(() => {
    if (profile) {
      setApiKey(profile.anthropic_api_key || '');
      setKimiApiKey(profile.kimi_api_key || '');
      setGlmApiKey(profile.glm_api_key || '');
      setMinimaxApiKey(profile.minimax_api_key || '');
      setAutoSubmitErrors(profile.auto_submit_errors || false);
      setNickname(profile.display_name || '');
      setUsername(profile.username || '');
      setIsPublicProfile(profile.is_public_profile || false);
    }
  }, [profile]);

  // Handle connect success/refresh URL params
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'success') {
      setConnectSuccess(true);
      refreshProfile();
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
    } else if (connectParam === 'refresh') {
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshProfile]);

  // Listen for openSettings event (redirects to this page now)
  useEffect(() => {
    const openSettingsHandler = () => {
      // Already on the profile page, just reset state
      setSaveSuccess(false);
      setSaveError(null);
      setConnectSuccess(false);
      clearConnectError();
    };
    window.addEventListener('openSettings', openSettingsHandler);
    return () => window.removeEventListener('openSettings', openSettingsHandler);
  }, [clearConnectError]);

  const handleConnectStripe = async () => {
    const result = await startOnboarding();
    if (result?.alreadyOnboarded) {
      setConnectSuccess(true);
      refreshProfile();
    }
  };

  const handleOpenStripeDashboard = async () => {
    await openDashboard();
  };

  const handleSave = async () => {
    const sanitizedKey = sanitizeApiKey(apiKey);
    const sanitizedKimiKey = sanitizeApiKey(kimiApiKey);
    const sanitizedGlmKey = sanitizeApiKey(glmApiKey);
    const sanitizedMinimaxKey = sanitizeApiKey(minimaxApiKey);
    const trimmedUsername = username.trim();

    if (sanitizedKey && !isValidApiKeyFormat(sanitizedKey)) {
      setSaveError('Invalid API key format. Anthropic API keys should start with "sk-ant-"');
      return;
    }

    if (trimmedUsername) {
      const usernameValidation = validateUsername(trimmedUsername);
      if (!usernameValidation.valid) {
        setSaveError(usernameValidation.error || 'Invalid username');
        setUsernameError(usernameValidation.error || 'Invalid username');
        return;
      }
    }

    if (isPublicProfile && !trimmedUsername) {
      setSaveError('Username is required to make your profile public');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setUsernameError(null);
    setSaveSuccess(false);

    const { error } = await updateProfile({
      anthropic_api_key: sanitizedKey || null,
      kimi_api_key: sanitizedKimiKey || null,
      glm_api_key: sanitizedGlmKey || null,
      minimax_api_key: sanitizedMinimaxKey || null,
      auto_submit_errors: autoSubmitErrors,
      display_name: nickname.trim() || null,
      username: trimmedUsername || null,
      is_public_profile: isPublicProfile,
    });

    setSaving(false);

    if (error) {
      if (error.message.includes('profiles_username_unique') || error.message.includes('duplicate key')) {
        setSaveError('This username is already taken');
        setUsernameError('This username is already taken');
      } else {
        setSaveError(error.message);
      }
    } else {
      setApiKey(sanitizedKey);
      setKimiApiKey(sanitizedKimiKey);
      setGlmApiKey(sanitizedGlmKey);
      setSaveSuccess(true);

      const pendingQuery = localStorage.getItem(PENDING_APP_QUERY_KEY);
      if (pendingQuery && (sanitizedKey || sanitizedKimiKey || sanitizedGlmKey || sanitizedMinimaxKey)) {
        setTimeout(() => navigate('/'), 1500);
      } else {
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    }
  };

  const handleRemoveApiKey = async () => {
    setSaving(true);
    setSaveError(null);

    const { error } = await updateProfile({ anthropic_api_key: null, kimi_api_key: null, glm_api_key: null, minimax_api_key: null });

    setSaving(false);

    if (error) {
      setSaveError(error.message);
    } else {
      setApiKey('');
      setKimiApiKey('');
      setGlmApiKey('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-ghost p-2"
            title={tt('Go back')}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-theme-text">{tt('Profile & Settings')}</h1>
        </div>

        <div className="space-y-8">
          {/* Account Identity */}
          <section className="card p-6 space-y-4">
            <h2 className="text-sm font-semibold text-theme-text">{tt('Account')}</h2>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Email')}
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-theme-surface border border-theme-border-muted">
                <span className="text-sm text-theme-text">{user?.email}</span>
              </div>
            </div>

            {/* Nickname */}
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Nickname')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-text-muted" />
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="input pl-10"
                  placeholder={tt('Your display name')}
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Username')}
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-text-muted" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\s/g, '');
                    setUsername(val);
                    if (val) {
                      const validation = validateUsername(val);
                      setUsernameError(validation.valid ? null : validation.error || null);
                    } else {
                      setUsernameError(null);
                    }
                  }}
                  className={`input pl-10 ${usernameError ? 'border-theme-error focus:ring-theme-error' : ''}`}
                  placeholder={tt('your_username')}
                  maxLength={15}
                />
              </div>
              {usernameError ? (
                <p className="mt-1.5 text-xs text-theme-error">{tt(usernameError)}</p>
              ) : (
                <p className="mt-1.5 text-xs text-theme-text-muted">
                  {tt('4-15 characters. Letters, numbers, and underscores only.')}
                </p>
              )}
              {username && !usernameError && (
                <p className="mt-1 text-xs text-theme-text-muted">
                  {tt('Your profile URL:')} <span className="text-theme-primary">llambada.com/u/{username}</span>
                </p>
              )}
            </div>

            {/* Public Profile Toggle */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublicProfile}
                  onChange={(e) => {
                    if (e.target.checked && !username.trim()) {
                      setSaveError(tt('Set a username first to make your profile public'));
                      return;
                    }
                    setIsPublicProfile(e.target.checked);
                  }}
                  className="mt-1 h-4 w-4 rounded border-theme-border-muted text-accent-500 focus:ring-accent-500"
                />
                <div>
                  <span className="text-sm font-medium text-theme-text">{tt('Public profile')}</span>
                  <p className="text-xs text-theme-text-muted mt-0.5">
                    {tt('Allow others to see your profile page and public mini-apps')}
                  </p>
                </div>
              </label>
              {profile?.is_public_profile && profile?.username && (
                <div className="mt-2 ml-7 flex items-center gap-2">
                  <LinkIcon className="h-3 w-3 text-theme-text-muted" />
                  <a
                    href={`/u/${profile.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-theme-primary hover:underline"
                  >
                    {tt('View your public profile')}
                  </a>
                </div>
              )}
            </div>

            {/* Country (read-only) */}
            {profile?.country && (
              <div>
                <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                  {tt('Country')}
                </label>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-theme-surface border border-theme-border-muted">
                  <Globe className="h-4 w-4 text-theme-text-muted" />
                  <span className="text-sm text-theme-text">{getCountryName(profile.country)}</span>
                </div>
                <p className="mt-1.5 text-xs text-theme-text-muted">
                  {tt('Country is set during registration and cannot be changed')}
                </p>
              </div>
            )}
          </section>

          {/* Creator Monetization */}
          {creatorFeaturesEnabled && (
            <section className="card p-6 space-y-4">
              <h2 className="text-sm font-semibold text-theme-text">{tt('Creator Monetization')}</h2>
              <p className="text-sm text-theme-text-secondary">
                {tt('Connect your bank account to receive payouts when users run your mini-apps.')}
                {' '}
                {tt("Earn 70% of subscription revenue based on your apps' usage.")}
              </p>

              {(connectSuccess || isConnected) ? (
                <div className="space-y-3">
                  {isChargesEnabled ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-theme-success-bg border border-theme-success-border text-theme-success">
                      <Check className="h-5 w-5" />
                      <span className="text-sm font-medium">{tt('Payouts Enabled')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400">
                      <Clock className="h-5 w-5" />
                      <span className="text-sm font-medium">{tt('Stripe Connected - Verification Pending')}</span>
                    </div>
                  )}
                  <button
                    onClick={handleOpenStripeDashboard}
                    disabled={connectLoading}
                    className="btn btn-secondary w-full"
                  >
                    {connectLoading ? <LoadingSpinner size="sm" /> : (
                      <>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        {tt('View Stripe Dashboard')}
                      </>
                    )}
                  </button>
                  {!isChargesEnabled && (
                    <p className="text-xs text-theme-text-muted text-center">
                      {tt('Check your Stripe dashboard to complete any required verification steps')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {connectError && (
                    <div className="p-3 rounded-lg bg-theme-error-bg border border-theme-error-border text-sm text-theme-error">
                      {connectError}
                    </div>
                  )}
                  <button
                    onClick={handleConnectStripe}
                    disabled={connectLoading}
                    className="btn btn-primary w-full"
                  >
                    {connectLoading ? <LoadingSpinner size="sm" /> : (
                      <>
                        <DollarSign className="h-4 w-4 mr-2" />
                        {tt('Connect Stripe for Payouts')}
                      </>
                    )}
                  </button>
                  <p className="text-xs text-theme-text-muted text-center">
                    {tt("You'll be redirected to Stripe to complete setup")}
                  </p>
                </div>
              )}
            </section>
          )}

          {/* Subscription */}
          <section className="card p-6 space-y-4">
            <h2 className="text-sm font-semibold text-theme-text">{tt('Subscription')}</h2>
            <div className="flex items-center justify-between p-3 rounded-lg bg-theme-surface border border-theme-border-muted">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-theme-text-muted" />
                <div>
                  <p className="text-sm font-medium text-theme-text">
                    {getTierDisplayName(subscriptionTier)}
                  </p>
                  <p className="text-xs text-theme-text-muted">
                    {subscriptionTier === 'free' ? tt('Basic features') :
                     subscriptionTier === 'standard_with_ads' ? tt('Standard features with ads') :
                     subscriptionTier === 'standard' ? tt('Full features, no ads') :
                     tt('All features, highest limits')}
                  </p>
                </div>
              </div>
              {PAYMENTS_DISABLED ? (
                <span className="btn btn-secondary text-sm opacity-50 cursor-default">
                  {tt('Payments Disabled')}
                </span>
              ) : (
                <Link
                  to="/pricing"
                  className="btn btn-secondary text-sm"
                >
                  {subscriptionTier === 'free' ? tt('Upgrade') : tt('Manage')}
                </Link>
              )}
            </div>
          </section>

          {/* API Keys */}
          <section className="card p-6 space-y-4">
            <h2 className="text-sm font-semibold text-theme-text">{tt('API Keys')}</h2>

            {isAdmin ? (
              <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20">
                <div className="flex items-center gap-2 text-accent-500 font-medium mb-2">
                  <Check className="h-5 w-5" />
                  {tt('Max Plan Active')}
                </div>
                <p className="text-sm text-theme-text-secondary">
                  {tt("You're using the coding AI with the Max plan. No API key required.")}
                </p>
              </div>
            ) : (
              <>
                {/* Anthropic */}
                <div>
                  <label className="block text-sm font-medium text-theme-text mb-2">
                    {tt('Anthropic API Key')}
                  </label>
                  <p className="text-sm text-theme-text-secondary mb-3">
                    {tt('Enter your Anthropic API key to use the coding AI. Get your key from')}{' '}
                    <a
                      href="https://platform.claude.com/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-500 hover:underline"
                    >
                      platform.claude.com
                    </a>
                  </p>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="input pr-10"
                      placeholder={tt('sk-ant-...')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-theme-text-muted hover:text-theme-text transition-colors"
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Kimi */}
                <div className="pt-4 border-t border-theme-border-muted">
                  <label className="block text-sm font-medium text-theme-text mb-2">
                    {tt('Kimi API Key')}
                  </label>
                  <p className="text-sm text-theme-text-secondary mb-3">
                    {tt('Enter your Kimi API key to use the coding AI. Get your key from')}{' '}
                    <a
                      href="https://www.kimi.com/code/console"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-500 hover:underline"
                    >
                      kimi.com
                    </a>
                  </p>
                  <div className="relative">
                    <input
                      type={showKimiApiKey ? 'text' : 'password'}
                      value={kimiApiKey}
                      onChange={(e) => setKimiApiKey(e.target.value)}
                      className="input pr-10"
                      placeholder={tt('sk-...')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKimiApiKey(!showKimiApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-theme-text-muted hover:text-theme-text transition-colors"
                    >
                      {showKimiApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* GLM */}
                <div className="pt-4 border-t border-theme-border-muted">
                  <label className="block text-sm font-medium text-theme-text mb-2">
                    {tt('GLM API Key')}
                  </label>
                  <p className="text-sm text-theme-text-secondary mb-3">
                    {tt('Enter your Z.ai GLM API key to use GLM coding models. Get your key from')}{' '}
                    <a
                      href="https://z.ai/model-api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-500 hover:underline"
                    >
                      z.ai
                    </a>
                  </p>
                  <div className="relative">
                    <input
                      type={showGlmApiKey ? 'text' : 'password'}
                      value={glmApiKey}
                      onChange={(e) => setGlmApiKey(e.target.value)}
                      className="input pr-10"
                      placeholder={tt('...')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowGlmApiKey(!showGlmApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-theme-text-muted hover:text-theme-text transition-colors"
                    >
                      {showGlmApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* MiniMax */}
                <div className="pt-4 border-t border-theme-border-muted">
                  <label className="block text-sm font-medium text-theme-text mb-2">
                    {tt('MiniMax API Key')}
                  </label>
                  <p className="text-sm text-theme-text-secondary mb-3">
                    {tt('Enter your MiniMax API key to use MiniMax M2.5 coding model. Get your key from')}{' '}
                    <a
                      href="https://platform.minimax.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-500 hover:underline"
                    >
                      platform.minimax.io
                    </a>
                  </p>
                  <div className="relative">
                    <input
                      type={showMinimaxApiKey ? 'text' : 'password'}
                      value={minimaxApiKey}
                      onChange={(e) => setMinimaxApiKey(e.target.value)}
                      className="input pr-10"
                      placeholder={tt('...')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowMinimaxApiKey(!showMinimaxApiKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-theme-text-muted hover:text-theme-text transition-colors"
                    >
                      {showMinimaxApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Preferences */}
          <section className="card p-6 space-y-4">
            <h2 className="text-sm font-semibold text-theme-text">{tt('Preferences')}</h2>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoSubmitErrors}
                onChange={(e) => setAutoSubmitErrors(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-theme-border-muted text-accent-500 focus:ring-accent-500"
              />
              <div>
                <span className="text-sm font-medium text-theme-text">{tt('Auto-submit errors to AI')}</span>
                <p className="text-xs text-theme-text-muted mt-0.5">
                  {tt('When enabled, mini-app errors will be automatically sent to AI for fixing without showing a confirmation dialog.')}
                </p>
              </div>
            </label>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Language')}
              </label>
              <LanguageSelector />
            </div>
          </section>

          {/* Save / Error / Success */}
          <div className="space-y-4">
            {saveError && (
              <div className="p-3 rounded-lg bg-theme-error-bg border border-theme-error-border text-sm text-theme-error">
                {tt(saveError)}
              </div>
            )}

            {saveSuccess && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-theme-success-bg border border-theme-success-border text-sm text-theme-success">
                <Check className="h-4 w-4" />
                {tt('Settings saved successfully')}
              </div>
            )}

            <div className="flex gap-3">
              {!isAdmin && (hasApiKey || hasKimiApiKey || hasGlmApiKey) && (
                <button
                  onClick={handleRemoveApiKey}
                  disabled={saving}
                  className="btn btn-secondary flex-1"
                >
                  {saving ? <LoadingSpinner size="sm" /> : tt('Remove All Keys')}
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary flex-1"
              >
                {saving ? <LoadingSpinner size="sm" /> : tt('Save Settings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
